<template>
    <div class="oauth-login">
        <template v-if="hasError">
            <div class="oauth-login__error-icon">
                <svg
                    width="36"
                    height="36"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                >
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
            </div>

            <h1 class="oauth-login__title">
                {{ $t('Connection not available') }}
            </h1>

            <p class="oauth-login__description">
                {{ $t('This site does not support OAuth or cannot connect to this site. Check the URL or contact the service administrator.') }}
            </p>

            <div class="oauth-login__back">
                <b-button variant="link" type="button" class="oauth-login__back-button" @click="goBack()">
                    {{ $t('Back') }}
                </b-button>
            </div>
        </template>

        <template v-else>
            <connect-icons
                :application-logo="application.logo"
                :application-name="application.name"
                :site-id="$route.query.site_id"
            />

            <h1 class="oauth-login__title">
                {{ $t('Connect with %s').replace('%s', titleAppName) }}
            </h1>

            <p class="oauth-login__description">
                {{ $t('%s wants to access your Accounts account to securely sign in.').replace('%s', titleAppName) }}
            </p>

            <div class="oauth-login__callout">
                <svg
                    class="oauth-login__callout-icon"
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                >
                    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/>
                </svg>
                <p class="oauth-login__callout-text">
                    <span>{{ calloutBefore }}</span>
                    <strong class="oauth-login__callout-highlight">{{ titleAppName }}</strong>
                    <span>{{ calloutAfter }}</span>
                </p>
            </div>

            <oauth-login-form
                ref="login"
                :show-finger.sync="showFinger"
                submit-text="Authorize and continue"
            />

            <div class="oauth-login__social-separator">
                <span class="oauth-login__social-separator-text">
                    {{ $t('Or continue with') }}
                </span>
            </div>

            <div class="oauth-login__social-buttons">
                <component :is="googleLoginComponent" :type="showFinger.status ? 'icon' : null" />
                <apple-login-button :type="showFinger.status ? 'icon' : null" />
                <web-authn-button v-if="!itsAnIframe && showFinger.status" :email="showFinger.email" />
            </div>
        </template>
    </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';

import AppleLoginButton from '@/components/ui/buttons/apple/AppleLoginButton.vue';
import GoogleLoginButton from '@/components/ui/GoogleLoginButton.vue';
import GoogleLoginButtonRedirection from '@/components/ui/buttons/google/GoogleLoginButtonRedirection.vue';
import GoogleLoginButtonPopup from '@/components/ui/buttons/google/GoogleLoginButtonPopup.vue';
import OauthLoginForm from '@/components/oauth/OAuthLoginForm.vue';
import WebAuthnButton from '@/components/authentication/webauthn/WebAuthnButton.vue';
import ConnectIcons from '@/components/oauth/ConnectIcons.vue';

import iframeMixin from '@/plugins/mixin/iframe.mixin';
import appleLoginMixin from '@/plugins/mixin/apple-login.mixin';

import { getSiteName } from '@/data/site-logos';

const EMPTY_APPLICATION = { name: null, description: null, logo: null };

const getClientId = (query = {}) => {
    if (query.client_id) {
        return query.client_id;
    }
    if (!query.redirect_url) {
        return null;
    }
    try {
        const decoded = atob(query.redirect_url);
        return new URL(decoded).searchParams.get('client_id');
    } catch {
        return null;
    }
};

const fetchApplication = async (clientId) => {
    if (!clientId) {
        return { application: EMPTY_APPLICATION, hasError: true };
    }

    try {
        const { data } = await Vue.axiosAccount.get(`/api/sites/applications/${clientId}`);
        return {
            application: {
                name: (data && data.name) || null,
                description: (data && data.description) || null,
                logo: (data && data.logo_url) || null,
            },
            hasError: false,
        };
    } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn('[oauth-login] application fetch failed', error);
        }
        return { application: EMPTY_APPLICATION, hasError: true };
    }
};

export default {
    name: 'oauth-login-page',
    components: {
        AppleLoginButton,
        ConnectIcons,
        GoogleLoginButton,
        GoogleLoginButtonRedirection,
        GoogleLoginButtonPopup,
        OauthLoginForm,
        WebAuthnButton,
    },
    mixins: [iframeMixin, appleLoginMixin],
    async beforeRouteEnter(to, from, next) {
        const { application, hasError } = await fetchApplication(getClientId(to.query));
        next(vm => {
            vm.application = application;
            vm.hasError = hasError;
        });
    },
    data() {
        return {
            application: EMPTY_APPLICATION,
            hasError: false,
            showFinger: {
                status: false,
                email: '',
            },
        };
    },
    computed: {
        ...mapGetters('ui', ['sites']),
        siteName() {
            return getSiteName(this.$route.query.site_id, this.sites);
        },
        titleAppName() {
            const name = (this.application.name || '').replace(/[^\p{L}\p{N}\s]/gu, '').trim();
            return name ? name.charAt(0).toUpperCase() + name.slice(1) : this.$t('the application');
        },
        calloutBefore() {
            const template = this.$t('By continuing, you authorize %s to read your basic profile information (name and email).');
            return template.split('%s')[0] || '';
        },
        calloutAfter() {
            const template = this.$t('By continuing, you authorize %s to read your basic profile information (name and email).');
            const parts = template.split('%s');
            return parts.length > 1 ? parts.slice(1).join('%s') : '';
        },
        googleLoginComponent() {
            const mode = this.$route.query.google_login_mode;
            const components = {
                default: 'GoogleLoginButton',
                redirection: 'GoogleLoginButtonRedirection',
                popup: 'GoogleLoginButtonPopup',
            };

            if (this.itsAnIframe && !mode) {
                return components.popup;
            }

            return components[mode] || components.default;
        },
    }
};
</script>

<style lang="scss">
.oauth-login {
    margin: 0 auto;
    max-width: 480px;
    padding: 16px;
    text-align: center;
    width: 100%;

    &__error-icon {
        align-items: center;
        background-color: #fdecec;
        border-radius: 24px;
        color: #ef4444;
        display: inline-flex;
        height: 96px;
        justify-content: center;
        margin: 12px auto 28px;
        width: 96px;
    }

    &__title {
        font-size: 28px;
        font-weight: 600;
        margin-bottom: 12px;
    }

    &__description {
        color: #7c7fa8;
        font-size: 16px;
        margin-bottom: 28px;
    }

    &__callout {
        align-items: flex-start;
        background-color: #e9eaf7;
        border-radius: 16px;
        display: flex;
        gap: 14px;
        margin-bottom: 32px;
        padding: 20px 24px;
        text-align: left;
    }

    &__callout-icon {
        color: #5eead4;
        flex-shrink: 0;
        height: 28px;
        margin-top: 2px;
        width: 28px;
    }

    &__callout-text {
        color: #4b5563;
        font-size: 15px;
        line-height: 1.45;
        margin: 0;
    }

    &__callout-highlight {
        color: #1f2937;
        font-weight: 700;
    }

    &__primary-button {
        margin-bottom: 8px;
        width: 100%;
    }

    &__social-separator {
        display: flex;
        justify-content: center;
        margin: 24px 0 16px;
        position: relative;
    }

    &__social-separator-text {
        color: #9093a1;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.08em;
        padding: 0 12px;
        text-transform: uppercase;

        &::before,
        &::after {
            background-color: #e5e7eb;
            content: '';
            height: 1px;
            position: absolute;
            top: 50%;
            width: calc(50% - 70px);
        }

        &::before {
            left: 0;
        }

        &::after {
            right: 0;
        }
    }

    &__social-buttons {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        justify-content: center;
        margin-bottom: 8px;
        width: 100%;
    }

    &__back {
        margin-top: 4px;
    }

    &__back-button {
        color: #7c7fa8 !important;
        font-weight: 500 !important;
    }
}
</style>
